import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Divider } from '@heroui/react';
import { useLanguage } from '../../stores/language-store';
import { useSidebar } from './sidebar-provider';
import { SidebarHeader } from './sidebar-header';
import { NavMain } from './nav-main';

export const AppSidebar: React.FC = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { isOpen, isMini, isMobile, toggleSidebar } = useSidebar();

  // Desktop variants - width based with explicit x:0 to reset mobile transforms
  const desktopVariants = {
    open: { x: 0, width: '280px', transition: { duration: 0.3, ease: 'easeInOut' } },
    mini: { x: 0, width: '70px', transition: { duration: 0.3, ease: 'easeInOut' } },
    closed: { x: 0, width: '0px', transition: { duration: 0.3, ease: 'easeInOut' } }
  };

  // Mobile variants - transform based (drawer) with explicit width
  const mobileVariants = {
    open: { 
      x: 0,
      width: '280px',
      transition: { duration: 0.3, ease: 'easeInOut' } 
    },
    closed: { 
      x: isRTL ? '100%' : '-100%',
      width: '280px',
      transition: { duration: 0.3, ease: 'easeInOut' } 
    }
  };

  const currentVariant = isOpen ? (isMini ? 'mini' : 'open') : 'closed';
  const variants = isMobile ? mobileVariants : desktopVariants;

  // Portal Sections Navigation
  const portalSectionsNav = [
    {
      title: t('imamIntro'),
      url: '/portal-sections/imam-intro',
      icon: 'lucide:book-open',
    },
    {
      title: t('prayers'),
      url: '/portal-sections/prayers/prayers-list',
      icon: 'lucide:heart',
      items: [
        { title: t('prayers'), url: '/portal-sections/prayers/prayers-list', icon: 'lucide:heart' },
        { title: t('prayerCategories'), url: '/portal-sections/prayers/categories', icon: 'lucide:folder' },
        { title: t('prayerReciters'), url: '/portal-sections/prayers/narrators', icon: 'lucide:user' },
        // { title: t('prayerReferences'), url: '/portal-sections/prayers/references', icon: 'lucide:book-open' },
        // { title: t('prayerParts'), url: '/portal-sections/prayers/prayer-parts', icon: 'lucide:file-text' },
      ],
    },
    {
      title: t('specialFiles'),
      url: '/portal-sections/special-files/special-files-list',
      icon: 'lucide:folder',
    },
    {
      title: t('news'),
      url: '/portal-sections/news',
      icon: 'lucide:rss',
    },
    {
      title: t('wiki'),
      url: '/portal-sections/wiki/wiki-list',
      icon: 'lucide:book-open',
      items: [
        { title: t('wikiEntries'), url: '/portal-sections/wiki/wiki-list', icon: 'lucide:book-open' },
        { title: t('wikiContents'), url: '/portal-sections/wiki/wiki-contents', icon: 'lucide:file-text' },
        { title: t('wikiCategories'), url: '/portal-sections/wiki/categories', icon: 'lucide:folder' },
        { title: t('wikiAuthors'), url: '/portal-sections/wiki/authors', icon: 'lucide:user' },
      ],
    },
    {
      title: t('library'),
      url: '/portal-sections/library/books',
      icon: 'lucide:library',
      items: [
        { title: t('books'), url: '/portal-sections/library/books', icon: 'lucide:book' },
        { title: t('authors'), url: '/portal-sections/library/authors', icon: 'lucide:user' },
        { title: t('publishers'), url: '/portal-sections/library/publishers', icon: 'lucide:building' },
        { title: t('categories'), url: '/portal-sections/library/categories', icon: 'lucide:folder' },
      ],
    },
    {
      title: t('hadisParts'),
      url: '/portal-sections/hadiths/hadis-parts',
      icon: 'lucide:quote',
      items: [
        { title: t('hadiths'), url: '/portal-sections/hadiths/hadiths-list', icon: 'lucide:quote' },
        { title: t('hadisCategories'), url: '/portal-sections/hadiths/categories', icon: 'lucide:folder' },
        { title: t('narrators'), url: '/portal-sections/hadiths/narrators', icon: 'lucide:user' },
        { title: t('references'), url: '/portal-sections/hadiths/references', icon: 'lucide:book-open' },
        { title: t('hadisParts'), url: '/portal-sections/hadiths/hadis-parts', icon: 'lucide:file-text' },
      ],
    },
    // {
    //   title: t('heartTalk'),
    //   url: '/portal-sections/heart-talk/heart-talk-list',
    //   icon: 'lucide:message-circle',
    //   items: [
    //     { title: t('heartTalks'), url: '/portal-sections/heart-talk/heart-talk-list', icon: 'lucide:message-circle' },
    //     { title: t('heartTalkCategories'), url: '/portal-sections/heart-talk/categories', icon: 'lucide:folder' },
    //     { title: t('heartTalkSpeakers'), url: '/portal-sections/heart-talk/authors', icon: 'lucide:user' },
    //   ],
    // },
    {
      title: t('magazines'),
      url: '/portal-sections/magazines',
      icon: 'lucide:newspaper',
    },
    {
      title: t('gallery'),
      url: '/portal-sections/gallery/media',
      icon: 'lucide:image',
      items: [
        { title: t('media'), url: '/portal-sections/gallery/media', icon: 'lucide:image' },
        { title: t('categories'), url: '/portal-sections/gallery/categories', icon: 'lucide:folder' },
      ],
    },
    {
      title: t('astanStructures') || 'Astan Structures',
      url: '/portal-sections/astan-structures',
      icon: 'lucide:network',
    },
    {
      title: t('contactUs'),
      url: '/portal-sections/contact-us',
      icon: 'lucide:mail',
    },
  ];

  // Management Navigation
  const managementNav = [
    {
      title: t('users'),
      url: '/users',
      icon: 'lucide:users',
    },
    {
      title: t('chat'),
      url: '/chat/conversations',
      icon: 'lucide:messages-square',
      items: [
        { title: t('conversations'), url: '/chat/conversations', icon: 'lucide:message-square-text' },
        { title: t('organizations'), url: '/chat/organizations', icon: 'lucide:building-2' },
      ],
    },
    {
      title: t('technicalSupport'),
      url: '/technical-support',
      icon: 'lucide:headphones',
    },
    {
      title: t('languages'),
      url: '/languages',
      icon: 'lucide:languages',
    },
    {
      title: t('settings'),
      url: '/settings',
      icon: 'lucide:settings',
    },
    // {
    //   title: t('statistics'),
    //   url: '/statistics',
    //   icon: 'lucide:bar-chart-2',
    // },
  ];

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && !isMini && isMobile && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      <motion.aside
        variants={variants}
        animate={currentVariant}
        initial={isMobile ? 'closed' : currentVariant}
        className={`fixed ${isRTL ? 'right-0 border-l' : 'left-0 border-r'} top-0 h-full bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 z-50 overflow-hidden flex flex-col ${isMobile ? 'w-[280px]' : ''}`}
      >
        {/* Header */}
        <SidebarHeader />

        {/* Navigation Content */}
        <div className={`flex-1 ${isMini ? 'overflow-hidden' : 'overflow-y-auto scrollbar-thin'}`}>
          <nav className="p-3 space-y-2">
            {/* Dashboard */}
            <NavMain
              items={[
                {
                  title: t('dashboard'),
                  url: '/dashboard',
                  icon: 'lucide:home',
                },
              ]}
            />

            <Divider className="my-3" />

            {/* Portal Sections */}
            <NavMain items={portalSectionsNav} groupLabel={t('portalSections')} />

            <Divider className="my-3" />

            {/* Management */}
            <NavMain items={managementNav} groupLabel={t('management')} />
          </nav>
        </div>
      </motion.aside>
    </>
  );
};